import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * Selo de status.
 *
 * Regra do CLAUDE.md: status NUNCA depende só de cor — o rótulo textual é
 * obrigatório, e é por isso que este componente não tem variante "só bolinha".
 * O amarelo (`brand`) é reservado a destaque de marca; para aviso use `warning`,
 * que é âmbar e vem acompanhado de ícone.
 */
const badgeVariants = cva(
  cn(
    'inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium',
    'whitespace-nowrap [&_svg]:size-3 [&_svg]:shrink-0',
  ),
  {
    variants: {
      variant: {
        neutral: 'border-border bg-elevated text-fg-secondary',
        brand: 'border-transparent bg-primary text-primary-foreground',
        outline: 'border-border-strong bg-transparent text-foreground',
        // Preenchimento com o hex vivo a 10%; texto e borda com o token `-fg`,
        // que o tema claro escurece. Escrever com `text-success` sobre o card
        // branco daria 2,3:1 — reprovado até para elemento não textual.
        success: 'border-success-fg/40 bg-success/10 text-success-fg',
        warning: 'border-warning-fg/40 bg-warning/10 text-warning-fg',
        danger: 'border-danger-fg/40 bg-danger/10 text-danger-fg',
        info: 'border-info-fg/40 bg-info/10 text-info-fg',
      },
    },
    defaultVariants: {
      variant: 'neutral',
    },
  },
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps): React.ReactElement {
  return <span data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />
}

export { Badge, badgeVariants }
